/**
 * Stock Monitor Component
 *
 * Real-time stock monitoring with live updates and alerts
 */

import {
  ArrowDownIcon,
  ArrowUpIcon,
  BellIcon,
  ClockIcon,
  EyeIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { InventoryWebSocketService } from '@/services/inventory-websocket.service';
import { InventoryItem } from '@/types/inventory';
import { formatDate, formatNumber } from '@/utils';

interface StockMonitorProps {
  inventoryItems: InventoryItem[];
  autoConnect?: boolean;
  showAlerts?: boolean;
  maxDisplayItems?: number;
  className?: string;
}

interface StockMovement {
  id: string;
  inventoryId: string;
  inventoryName: string;
  type: 'in' | 'out' | 'reserved' | 'released' | 'adjusted';
  quantity: number;
  previousQuantity: number;
  newQuantity: number;
  reason?: string;
  reference?: string;
  timestamp: string;
  user?: string;
}

interface StockAlert {
  id: string;
  type: 'low_stock' | 'out_of_stock' | 'over_stock' | 'reservation_expired';
  inventoryId: string;
  inventoryName: string;
  currentQuantity: number;
  threshold?: number;
  severity: 'info' | 'warning' | 'error';
  timestamp: string;
  acknowledged: boolean;
}

export const StockMonitor: React.FC<StockMonitorProps> = ({
  inventoryItems: _inventoryItems,
  autoConnect = true,
  showAlerts = true,
  maxDisplayItems = 50,
  className = '',
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([]);
  const [stockAlerts, setStockAlerts] = useState<StockAlert[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<
    'all' | 'in' | 'out' | 'reserved' | 'adjusted'
  >('all');

  // WebSocket service instance
  const wsService = useMemo(() => new InventoryWebSocketService(), []);

  useEffect(() => {
    if (autoConnect) {
      // Connect to WebSocket
      wsService.connect();
      setIsConnected(true);

      // Subscribe to stock movements
      wsService.subscribeToStockMovements((movement: any) => {
        setStockMovements(prev => [
          movement,
          ...prev.slice(0, maxDisplayItems - 1),
        ]);
      });

      // Subscribe to stock alerts
      wsService.subscribeToStockAlerts((alert: any) => {
        setStockAlerts(prev => [alert, ...prev.slice(0, 19)]); // Keep max 20 alerts
      });

      // Subscribe to realtime metrics
      wsService.subscribeToRealtimeMetrics((_metrics: any) => {
        // Handle realtime metrics
      });
    }

    return () => {
      wsService.disconnect();
      setIsConnected(false);
    };
  }, [autoConnect, wsService, maxDisplayItems]);

  // Filter movements based on selected filter
  const filteredMovements = useMemo(() => {
    if (selectedFilter === 'all') return stockMovements;
    return stockMovements.filter(movement => movement.type === selectedFilter);
  }, [stockMovements, selectedFilter]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const last24Hours = Date.now() - 24 * 60 * 60 * 1000;
    const recent = stockMovements.filter(
      m => new Date(m.timestamp).getTime() > last24Hours
    );

    const totalIn = recent
      .filter(m => m.type === 'in')
      .reduce((sum, m) => sum + m.quantity, 0);

    const totalOut = recent
      .filter(m => m.type === 'out')
      .reduce((sum, m) => sum + m.quantity, 0);

    const totalReserved = recent
      .filter(m => m.type === 'reserved')
      .reduce((sum, m) => sum + m.quantity, 0);

    const activeAlerts = stockAlerts.filter(a => !a.acknowledged);
    const criticalAlerts = activeAlerts.filter(a => a.severity === 'error');

    return {
      movements24h: recent.length,
      totalIn,
      totalOut,
      totalReserved,
      activeAlerts: activeAlerts.length,
      criticalAlerts: criticalAlerts.length,
      netMovement: totalIn - totalOut,
    };
  }, [stockMovements, stockAlerts]);

  // Get movement type styling
  const getMovementTypeStyle = (type: StockMovement['type']) => {
    const styles = {
      in: {
        icon: ArrowUpIcon,
        color: 'text-green-600',
        bgColor: 'bg-green-100',
      },
      out: {
        icon: ArrowDownIcon,
        color: 'text-red-600',
        bgColor: 'bg-red-100',
      },
      reserved: {
        icon: ClockIcon,
        color: 'text-orange-600',
        bgColor: 'bg-orange-100',
      },
      released: {
        icon: ClockIcon,
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
      },
      adjusted: {
        icon: EyeIcon,
        color: 'text-purple-600',
        bgColor: 'bg-purple-100',
      },
    };
    return styles[type] || styles.adjusted;
  };

  // Get alert severity styling
  const getAlertSeverityStyle = (severity: StockAlert['severity']) => {
    const styles = {
      info: { color: 'text-blue-600', bgColor: 'bg-blue-100' },
      warning: { color: 'text-orange-600', bgColor: 'bg-orange-100' },
      error: { color: 'text-red-600', bgColor: 'bg-red-100' },
    };
    return styles[severity];
  };

  // Acknowledge alert
  const acknowledgeAlert = (alertId: string) => {
    setStockAlerts(prev =>
      prev.map(alert =>
        alert.id === alertId ? { ...alert, acknowledged: true } : alert
      )
    );
  };

  // Connection status indicator
  const ConnectionStatus = () => (
    <div className='flex items-center gap-2'>
      <div
        className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}
      />
      <span className='text-sm text-gray-600'>
        {isConnected ? 'Connected' : 'Disconnected'}
      </span>
    </div>
  );

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with Connection Status */}
      <div className='flex items-center justify-between'>
        <h2 className='text-lg font-semibold'>Stock Monitor</h2>
        <ConnectionStatus />
      </div>

      {/* Summary Statistics */}
      <div className='grid grid-cols-1 md:grid-cols-6 gap-4'>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Movements (24h)
            </div>
            <div className='text-2xl font-bold'>
              {summaryStats.movements24h}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Stock In</div>
            <div className='text-2xl font-bold text-green-600'>
              +{formatNumber(summaryStats.totalIn)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Stock Out</div>
            <div className='text-2xl font-bold text-red-600'>
              -{formatNumber(summaryStats.totalOut)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>Reserved</div>
            <div className='text-2xl font-bold text-orange-600'>
              {formatNumber(summaryStats.totalReserved)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Active Alerts
            </div>
            <div className='text-2xl font-bold text-blue-600'>
              {summaryStats.activeAlerts}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className='p-4'>
            <div className='text-sm font-medium text-gray-500'>
              Net Movement
            </div>
            <div
              className={`text-2xl font-bold ${
                summaryStats.netMovement > 0
                  ? 'text-green-600'
                  : summaryStats.netMovement < 0
                    ? 'text-red-600'
                    : 'text-gray-600'
              }`}
            >
              {summaryStats.netMovement > 0 ? '+' : ''}
              {formatNumber(summaryStats.netMovement)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Critical Alerts */}
      {summaryStats.criticalAlerts > 0 && (
        <Alert className='border-red-200 bg-red-50'>
          <WarningIcon className='h-4 w-4 text-red-600' />
          <AlertDescription>
            <strong>
              {summaryStats.criticalAlerts} critical stock alert
              {summaryStats.criticalAlerts !== 1 ? 's' : ''}
            </strong>{' '}
            require immediate attention.
          </AlertDescription>
        </Alert>
      )}

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Stock Movements */}
        <Card>
          <CardHeader>
            <div className='flex items-center justify-between'>
              <CardTitle className='flex items-center gap-2'>
                <ArrowUpIcon className='h-5 w-5 text-blue-500' />
                Stock Movements
              </CardTitle>
              <div className='flex gap-1'>
                {['all', 'in', 'out', 'reserved', 'adjusted'].map(filter => (
                  <Button
                    key={filter}
                    hierarchy='outlined'
                    size='sm'
                    onClick={() => setSelectedFilter(filter as any)}
                    className={selectedFilter === filter ? 'bg-blue-100' : ''}
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className='h-96'>
              <div className='space-y-3'>
                {filteredMovements.length === 0 ? (
                  <div className='text-center text-gray-500 py-8'>
                    No stock movements to display
                  </div>
                ) : (
                  filteredMovements.map(movement => {
                    const style = getMovementTypeStyle(movement.type);
                    const Icon = style.icon;

                    return (
                      <div
                        key={movement.id}
                        className='flex items-start gap-3 p-3 rounded-lg border hover:bg-gray-50'
                      >
                        <div className={`p-2 rounded-full ${style.bgColor}`}>
                          <Icon className={`h-4 w-4 ${style.color}`} />
                        </div>
                        <div className='flex-1 min-w-0'>
                          <div className='flex items-center justify-between'>
                            <h4 className='font-medium text-gray-900 truncate'>
                              {movement.inventoryName}
                            </h4>
                            <Badge
                              className={style.bgColor + ' ' + style.color}
                            >
                              {movement.type}
                            </Badge>
                          </div>
                          <div className='text-sm text-gray-600 mt-1'>
                            <div className='flex items-center justify-between'>
                              <span>
                                Quantity:{' '}
                                <span className={`font-medium ${style.color}`}>
                                  {movement.type === 'out' ? '-' : '+'}
                                  {formatNumber(movement.quantity)}
                                </span>
                              </span>
                              <span className='text-xs'>
                                {formatDate(movement.timestamp)}
                              </span>
                            </div>
                            {movement.reason && (
                              <div className='text-xs text-gray-500 mt-1'>
                                Reason: {movement.reason}
                              </div>
                            )}
                            {movement.reference && (
                              <div className='text-xs text-gray-500'>
                                Ref: {movement.reference}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Stock Alerts */}
        {showAlerts && (
          <Card>
            <CardHeader>
              <CardTitle className='flex items-center gap-2'>
                <BellIcon className='h-5 w-5 text-orange-500' />
                Stock Alerts
                {summaryStats.activeAlerts > 0 && (
                  <Badge className='bg-orange-100 text-orange-800'>
                    {summaryStats.activeAlerts}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className='h-96'>
                <div className='space-y-3'>
                  {stockAlerts.length === 0 ? (
                    <div className='text-center text-gray-500 py-8'>
                      No stock alerts
                    </div>
                  ) : (
                    stockAlerts.map(alert => {
                      const style = getAlertSeverityStyle(alert.severity);

                      return (
                        <div
                          key={alert.id}
                          className={`p-3 rounded-lg border ${
                            alert.acknowledged ? 'opacity-50' : ''
                          }`}
                        >
                          <div className='flex items-start justify-between'>
                            <div className='flex-1'>
                              <div className='flex items-center gap-2 mb-1'>
                                <Badge
                                  className={`${style.bgColor} ${style.color}`}
                                >
                                  {alert.type.replace('_', ' ')}
                                </Badge>
                                <span className='text-sm text-gray-500'>
                                  {formatDate(alert.timestamp)}
                                </span>
                              </div>
                              <h4 className='font-medium text-gray-900'>
                                {alert.inventoryName}
                              </h4>
                              <div className='text-sm text-gray-600 mt-1'>
                                Current stock:{' '}
                                {formatNumber(alert.currentQuantity)}
                                {alert.threshold && (
                                  <span>
                                    {' '}
                                    (Threshold: {formatNumber(alert.threshold)})
                                  </span>
                                )}
                              </div>
                            </div>
                            {!alert.acknowledged && (
                              <Button
                                hierarchy='outlined'
                                size='sm'
                                onClick={() => acknowledgeAlert(alert.id)}
                              >
                                Acknowledge
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
